import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, BellRing, AlertCircle, CalendarClock, CheckCircle2, ArrowRight,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { DASH } from "@/constants/testIds";
import { formatDateTimeIt } from "@/lib/format";
import ReminderDialog from "@/components/app/ReminderDialog";
import SecurityBanner from "@/components/app/SecurityBanner";
import { toast } from "sonner";

const KpiCard = ({ label, value, sub, icon: Icon, tone = "orange", testid, onClick }) => {
  const tones = {
    orange: "bg-[#F56A1F] text-white",
    dark: "bg-stone-900 text-white",
    amber: "bg-amber-500 text-white",
    teal: "bg-teal-600 text-white",
  };
  return (
    <Card data-testid={testid} onClick={onClick}
      className="card-hover p-0 overflow-hidden border-stone-200 shadow-sm rounded-2xl cursor-pointer">
      <div className={`${tones[tone]} grain-orange px-5 py-3 flex items-center justify-between`}>
        <div className="text-[13px] font-medium tracking-wide">{label}</div>
        <Icon className="w-4 h-4 opacity-90" strokeWidth={1.75} />
      </div>
      <div className="p-6">
        <div className="text-4xl font-semibold text-stone-900 tracking-tighter">{value}</div>
        {sub && <div className="text-sm text-stone-500 mt-1">{sub}</div>}
      </div>
    </Card>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState({ today: [], overdue: [], upcoming: [] });
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [s, today, overdue, upcoming] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/reminders", { params: { filter: "today" } }),
      api.get("/reminders", { params: { filter: "overdue" } }),
      api.get("/reminders", { params: { filter: "upcoming" } }),
    ]);
    setStats(s.data);
    setReminders({ today: today.data, overdue: overdue.data, upcoming: upcoming.data.slice(0, 8) });
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("crm:data-changed", h);
    return () => window.removeEventListener("crm:data-changed", h);
  }, [load]);

  const toggle = async (id) => {
    try {
      await api.post(`/reminders/${id}/toggle`);
      toast.success("Richiamo aggiornato");
      load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const catData = useMemo(() => {
    if (!stats) return [];
    const c = stats.clienti.per_categoria || {};
    return [
      { name: "Pubbl. Amm.", value: c.pubblica_amministrazione || 0, color: "#2563EB" },
      { name: "Az. Edile", value: c.azienda_edile || 0, color: "#F56A1F" },
      { name: "Rivenditore", value: c.rivenditore || 0, color: "#059669" },
      { name: "Privato", value: c.privato || 0, color: "#6366F1" },
      { name: "Conto Terzi", value: c.conto_terzi || 0, color: "#9333EA" },
      { name: "Carpenteria", value: c.carpenteria || 0, color: "#D97706" },
      { name: "Da Categorizzare", value: c.altro || 0, color: "#A8A29E" },
    ];
  }, [stats]);

  const statiData = useMemo(() => {
    if (!stats) return [];
    return [
      { name: "Attivi", value: stats.clienti.attivi, color: "#10B981" },
      { name: "Potenziali", value: stats.clienti.potenziali, color: "#F59E0B" },
      { name: "Persi", value: stats.clienti.persi, color: "#A8A29E" },
    ];
  }, [stats]);

  return (
    <div className="p-6 lg:p-8 max-w-[1600px]">
      <SecurityBanner />
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.16em] text-[#F56A1F] font-semibold mb-2">
          Pannello di Controllo
        </div>
        <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight text-stone-900">Dashboard</h1>
        <p className="text-stone-500 mt-2 max-w-2xl">
          Panoramica sui clienti e sui richiami calendarizzati. I promemoria di oggi e quelli scaduti sono sempre in primo piano.
        </p>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <KpiCard
          testid={DASH.kpiClientiTot}
          label="Clienti Totali"
          value={stats?.clienti.totale ?? "—"}
          sub={`${stats?.clienti.attivi ?? 0} attivi · ${stats?.clienti.potenziali ?? 0} potenziali`}
          icon={Users}
          tone="dark"
          onClick={() => navigate("/clienti")}
        />
        <KpiCard
          testid={DASH.kpiRichiamiOggi}
          label="Richiami Oggi"
          value={stats?.richiami.oggi ?? "—"}
          sub="Da gestire in giornata"
          icon={BellRing}
          tone="orange"
          onClick={() => navigate("/richiami?tab=today")}
        />
        <KpiCard
          testid={DASH.kpiRichiamiScaduti}
          label="Richiami Scaduti"
          value={stats?.richiami.scaduti ?? "—"}
          sub="Richiedono azione immediata"
          icon={AlertCircle}
          tone="amber"
          onClick={() => navigate("/richiami?tab=overdue")}
        />
        <KpiCard
          testid={DASH.kpiRichiamiSettimana}
          label="Prossimi 7 giorni"
          value={stats?.richiami.settimana ?? "—"}
          sub="In programma questa settimana"
          icon={CalendarClock}
          tone="teal"
          onClick={() => navigate("/calendario")}
        />
      </div>

      {/* Main grid: reminders + charts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Reminders panel */}
        <Card className="lg:col-span-8 border-stone-200 shadow-sm rounded-2xl overflow-hidden">
          <div className="grain-orange bg-[#F56A1F] px-6 py-3 flex items-center justify-between">
            <div className="text-white font-medium">Richiami in Dashboard</div>
            <Button
              size="sm" variant="ghost"
              onClick={() => navigate("/richiami")}
              className="text-white/90 hover:text-white hover:bg-white/10 gap-1">
              Vedi tutti <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="p-6 space-y-6">
            <ReminderSection
              title="Scaduti"
              tone="danger"
              items={reminders.overdue}
              onToggle={toggle}
              onEdit={(r) => { setEditing(r); setDialogOpen(true); }}
              navigate={navigate}
              emptyLabel="Nessun richiamo scaduto — ottimo lavoro."
            />
            <ReminderSection
              title="Oggi"
              tone="today"
              items={reminders.today}
              onToggle={toggle}
              onEdit={(r) => { setEditing(r); setDialogOpen(true); }}
              navigate={navigate}
              emptyLabel="Nessun richiamo per oggi."
            />
            <ReminderSection
              title="Prossimi"
              tone="upcoming"
              items={reminders.upcoming}
              onToggle={toggle}
              onEdit={(r) => { setEditing(r); setDialogOpen(true); }}
              navigate={navigate}
              emptyLabel="Nessun richiamo programmato."
            />
          </div>
        </Card>

        {/* Charts column */}
        <div className="lg:col-span-4 space-y-5">
          <Card data-testid={DASH.chartCategorie} className="border-stone-200 shadow-sm rounded-2xl overflow-hidden">
            <div className="grain-orange bg-[#F56A1F] px-6 py-3">
              <div className="text-white font-medium">Categorie Cliente</div>
            </div>
            <div className="p-6">
              <div className="h-44">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={catData}
                      dataKey="value"
                      innerRadius={45}
                      outerRadius={70}
                      paddingAngle={2}
                      strokeWidth={0}
                    >
                      {catData.map((e) => <Cell key={e.name} fill={e.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E7E5E4", background: "#fff" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-2">
                {catData.map((d) => (
                  <div key={d.name} className="flex items-center gap-2 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                    <span className="text-stone-600 truncate">{d.name}</span>
                    <span className="text-stone-900 font-semibold ml-auto">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card data-testid={DASH.chartStati} className="border-stone-200 shadow-sm rounded-2xl overflow-hidden">
            <div className="grain-orange bg-[#F56A1F] px-6 py-3">
              <div className="text-white font-medium">Distribuzione Stati</div>
            </div>
            <div className="p-6">
              <div className="h-44">
                <ResponsiveContainer>
                  <BarChart data={statiData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F4" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#78716C" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#78716C" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E7E5E4", background: "#fff" }} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {statiData.map((e) => <Cell key={e.name} fill={e.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <ReminderDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        initial={editing}
        onSaved={load}
      />
    </div>
  );
}

function ReminderSection({ title, items, onToggle, onEdit, navigate, emptyLabel, tone }) {
  const dotColor = tone === "danger" ? "bg-red-500" : tone === "today" ? "bg-[#F56A1F]" : "bg-teal-500";
  const label =
    tone === "danger" ? "SCADUTO" :
    tone === "today" ? "OGGI" : "PROSSIMO";
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2 h-2 rounded-full ${dotColor}`} />
        <div className="text-xs uppercase tracking-[0.14em] font-semibold text-stone-500">{title}</div>
        <div className="text-xs text-stone-400">· {items.length}</div>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-stone-400 pl-4">{emptyLabel}</div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <div
              key={r.id}
              data-testid={DASH.reminderCard(r.id)}
              className="group flex items-start gap-3 p-3 rounded-xl border border-stone-100 hover:border-stone-200 hover:bg-stone-50/60 transition-colors cursor-pointer"
              onClick={() => onEdit(r)}
            >
              <button
                data-testid={DASH.reminderToggle(r.id)}
                onClick={(e) => { e.stopPropagation(); onToggle(r.id); }}
                className="mt-0.5 w-5 h-5 rounded-full border-2 border-stone-300 hover:border-[#F56A1F] flex items-center justify-center transition-colors"
                title="Segna come completato"
              >
                <CheckCircle2 className="w-4 h-4 text-transparent group-hover:text-stone-300 transition-colors" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-stone-900 truncate">{r.titolo}</div>
                  <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${
                    r.priorita === "alta" ? "border-red-200 bg-red-50 text-red-700" :
                    r.priorita === "bassa" ? "border-stone-200 bg-stone-50 text-stone-600" :
                    "border-amber-200 bg-amber-50 text-amber-700"
                  }`}>{label}</Badge>
                </div>
                {r.client_ragione_sociale && (
                  <div
                    className="text-xs text-[#F56A1F] mt-0.5 hover:underline"
                    onClick={(e) => { e.stopPropagation(); navigate(`/clienti/${r.client_id}`); }}
                  >
                    {r.client_ragione_sociale}
                  </div>
                )}
                {r.descrizione && (
                  <div className="text-sm text-stone-500 mt-1 line-clamp-2">{r.descrizione}</div>
                )}
              </div>
              <div className="text-xs text-stone-400 font-mono whitespace-nowrap">
                {formatDateTimeIt(r.scheduled_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
